/**
 * ShadcnDetailViewLayout
 *
 * shadcn/Tailwind replacement for EntityDetailViewLayout.
 * Read-only detail view with collapsible sections and a 3-column
 * responsive property grid. Uses the same field categorization logic.
 */

import React, { useRef, useState } from 'react';
import {
    ChevronDown,
    Copy,
    Check,
    ExternalLink,
    Fingerprint,
    MapPin,
    List,
    Settings,
    Clock,
    Mail,
    Briefcase,
    KeyRound,
    Users,
    Shield,
    GraduationCap,
    SlidersHorizontal,
    FileText,
    Eye,
    Code2,
    Tag,
    Info,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { getEntityValueFromARef } from '../../types';
import type { FormAttribute } from './EntityUpsertForm.types';
import { getAttributeName, getAttributeLabel } from '../../utils/schemaAttributeUtils';
import { extractReferenceDisplayName, extractReferenceId } from '../../utils/referenceUtils';
import { isContent, isEmailTemplate } from '../../utils/entityTypeDetection';
import { ShadcnMarkdownPreview } from '../common/ShadcnMarkdownPreview';
import { ScimAceEditor } from '../common/ScimAceEditor';
import { NON_INDEXED_PROPERTY_STUB } from '../../utils/entityPropertyUtils';

interface ShadcnDetailViewLayoutProps {
    schemaAttributes: FormAttribute[];
    formData: Record<string, any>;
    entityType: string;
    isStandard?: boolean;
}

/* ── helpers ── */

function normKey(raw: string): string {
    return (raw ?? '').toString().trim().toLowerCase().replace(/^\$\./, '');
}

/* ── field categorization (same keys as EntityDetailViewLayout) ── */

type FieldCategory =
    | 'identity' | 'account' | 'contact' | 'employment' | 'student'
    | 'address' | 'access' | 'membership' | 'configuration'
    | 'other' | 'system' | 'timestamp';

const IDENTITY_KEYS = new Set([
    'displayname', 'name', 'description', 'firstname', 'lastname', 'middleinitial',
    'email', 'personalemail', 'secondaryemail', 'username',
]);
const ACCOUNT_KEYS = new Set(['userprincipalname', 'accountname', 'accountstatus', 'domain']);
const CONTACT_KEYS = new Set([
    'homeaddress', 'homecity', 'homestate', 'homezip', 'homephone',
    'mobilephone', 'officephone', 'apt', 'phone', 'fax',
]);
const EMPLOYMENT_KEYS = new Set([
    'jobcode', 'jobtitle', 'department', 'position', 'employeenumber',
    'union', 'role', 'academicrole', 'manager', 'manages', 'sponsor', 'sponsors',
    'vendor', 'startdate', 'enddate', 'rc', 'rcs', 'rcjobcode', 'rcjobcodes',
]);
const STUDENT_KEYS = new Set([
    'gradelevel', 'gradelevels', 'enrollmentcode', 'ellcode', 'esecode',
    'magnet', 'spa', 'school', 'schools', 'classes', 'sections',
    'teachers', 'students', 'guardians', 'busstops', 'transportneeds',
]);
const ADDRESS_KEYS = new Set([
    'address', 'streetaddress', 'city', 'state', 'zipcode', 'postalcode',
    'country', 'latitude', 'longitude', 'zip',
]);
const ACCESS_KEYS = new Set([
    'accessperpetual', 'accessweekday', 'accessweekend', 'perpetualaccess',
    'weekdayaccess', 'weekendaccess', 'building', 'buildingmanager',
    'buildingassistant', 'assistant', 'backupassistant', 'backupmanager',
    'password', 'carddata', 'credential',
]);
const MEMBERSHIP_KEYS = new Set([
    'member', 'memberof', 'memberoftransitive', 'membertransitive',
    'owner', 'owners', 'principalof', 'schoolprincipalof',
]);
const CONFIGURATION_KEYS = new Set([
    'scope', 'managedtype', 'filterscope', 'filter', 'sqlstatement',
    'sqlstatementexplicit', 'clientid', 'tenantid', 'legacyfilter',
    'legacyfilterscope', 'legacyfilterattributes', 'legacyfilterfunctions',
    'legacyfilterliterals', 'compilationstate', 'requestworkflow',
    'authorizationworkflow', 'targetpostopset', 'relativetargetattribute',
]);
const SYSTEM_KEYS = new Set([
    'id', 'entitytype', 'type', 'managed', 'issystem', 'disabled',
    'onpremid', 'objectsid', 'entraid', 'objectid', 'version',
    'sapuser', 'sapuserid', 'unique', 'entryid',
]);
const TIMESTAMP_KEYS = new Set([
    'created', 'modified', 'recorded', 'lastmodified', 'createdon', 'modifiedon',
]);

function categorizeField(key: string): FieldCategory {
    const k = normKey(key);
    if (IDENTITY_KEYS.has(k)) return 'identity';
    if (ACCOUNT_KEYS.has(k)) return 'account';
    if (CONTACT_KEYS.has(k)) return 'contact';
    if (EMPLOYMENT_KEYS.has(k)) return 'employment';
    if (STUDENT_KEYS.has(k)) return 'student';
    if (ADDRESS_KEYS.has(k)) return 'address';
    if (ACCESS_KEYS.has(k)) return 'access';
    if (MEMBERSHIP_KEYS.has(k)) return 'membership';
    if (CONFIGURATION_KEYS.has(k)) return 'configuration';
    if (SYSTEM_KEYS.has(k)) return 'system';
    if (TIMESTAMP_KEYS.has(k)) return 'timestamp';
    return 'other';
}

interface SectionMeta {
    label: string;
    icon: React.FC<any>;
    accentColor: string;
    defaultOpen: boolean;
}

const SECTION_META: Record<FieldCategory, SectionMeta> = {
    identity:      { label: 'Identity',             icon: Fingerprint,       accentColor: '#2563EB', defaultOpen: true },
    account:       { label: 'Account',              icon: KeyRound,          accentColor: '#0891B2', defaultOpen: true },
    contact:       { label: 'Contact Information',  icon: Mail,              accentColor: '#DB2777', defaultOpen: true },
    employment:    { label: 'Employment',           icon: Briefcase,         accentColor: '#4F46E5', defaultOpen: true },
    student:       { label: 'Student Information',  icon: GraduationCap,     accentColor: '#7C3AED', defaultOpen: true },
    address:       { label: 'Location & Address',   icon: MapPin,            accentColor: '#0D9488', defaultOpen: true },
    access:        { label: 'Access & Security',    icon: Shield,            accentColor: '#DC2626', defaultOpen: true },
    membership:    { label: 'Membership',           icon: Users,             accentColor: '#65A30D', defaultOpen: false },
    configuration: { label: 'Configuration',        icon: SlidersHorizontal, accentColor: '#7C3AED', defaultOpen: false },
    other:         { label: 'Properties',           icon: List,              accentColor: '#7C3AED', defaultOpen: true },
    system:        { label: 'System',               icon: Settings,          accentColor: '#6B7280', defaultOpen: false },
    timestamp:     { label: 'Timestamps',           icon: Clock,             accentColor: '#EA580C', defaultOpen: false },
};

const SECTION_ORDER: FieldCategory[] = [
    'identity', 'account', 'contact', 'employment', 'student',
    'address', 'access', 'membership', 'configuration',
    'other', 'system', 'timestamp',
];

/* ── value helpers ── */

function resolveDisplayValue(raw: unknown): string {
    if (raw === null || raw === undefined || raw === '') return '\u2014';
    if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
    if (typeof raw === 'number') return String(raw);
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && !Array.isArray(raw)) {
        const dn = extractReferenceDisplayName(raw);
        if (dn) return dn;
        const id = extractReferenceId(raw);
        if (id) return id;
        const obj = raw as Record<string, unknown>;
        if (obj.DisplayName) return String(obj.DisplayName);
        if (obj.displayName) return String(obj.displayName);
        if (obj.Value || obj.value) return String(obj.Value ?? obj.value);
        try { return JSON.stringify(raw); } catch { return String(raw); }
    }
    if (Array.isArray(raw)) {
        if (raw.length === 0) return '\u2014';
        return raw.map(item => {
            if (typeof item === 'string') return item;
            return extractReferenceDisplayName(item) || extractReferenceId(item) || String(item);
        }).join(', ');
    }
    return String(raw);
}

function isLongValue(value: unknown): boolean {
    return resolveDisplayValue(value).length > 90;
}

function isBooleanField(attr: FormAttribute, value: unknown): boolean {
    const type = ((attr as any).type || '').toLowerCase();
    if (type === 'boolean') return true;
    if (typeof value === 'boolean') return true;
    const name = normKey(getAttributeName(attr));
    return name === 'disabled' || name === 'issystem' || name === 'managed'
        || name === 'required' || name === 'multivalued' || name === 'acknowledged';
}

function isIdLikeField(attr: FormAttribute): boolean {
    const name = normKey(getAttributeName(attr));
    return name === 'id' || name === 'objectid' || name === 'clientid'
        || name === 'tenantid' || name === 'entraid' || name === 'onpremid' || name === 'objectsid';
}

function isReferenceValue(value: unknown): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const obj = value as Record<string, unknown>;
    return !!(obj['$ref'] || obj.$ref || (obj.Id && obj.DisplayName));
}

/* ── field value renderer ── */

function FieldValue({ attr, value }: { attr: FormAttribute; value: unknown }) {
    const displayValue = resolveDisplayValue(value);
    const isEmpty = displayValue === '\u2014';

    if (isBooleanField(attr, value)) {
        const boolVal = value === true || value === 'true' || value === 'True';
        return (
            <span className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                boolVal
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
            )}>
                <span className={cn(
                    'w-1.5 h-1.5 rounded-full',
                    boolVal ? 'bg-emerald-500' : 'bg-gray-400',
                )} />
                {boolVal ? 'Active' : 'Inactive'}
            </span>
        );
    }

    if (isIdLikeField(attr) && !isEmpty) {
        return <CopyableValue value={displayValue} />;
    }

    if (isReferenceValue(value)) {
        return (
            <span className="inline-flex items-center gap-1 text-sm text-foreground">
                {extractReferenceDisplayName(value) || extractReferenceId(value) || displayValue}
                <ExternalLink size={11} className="text-muted-foreground/40 shrink-0" />
            </span>
        );
    }

    return (
        <span className={cn('text-sm', isEmpty ? 'text-muted-foreground/40' : 'text-foreground')}>
            {displayValue}
        </span>
    );
}

function CopyableValue({ value }: { value: string }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    return (
        <span className="inline-flex items-center gap-1.5 group/copy">
            <span className="text-sm font-mono text-foreground">{value}</span>
            <button
                onClick={handleCopy}
                className="opacity-0 group-hover/copy:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted"
                title={copied ? 'Copied!' : 'Copy'}
            >
                {copied
                    ? <Check size={12} className="text-emerald-500" />
                    : <Copy size={12} className="text-muted-foreground" />
                }
            </button>
        </span>
    );
}

/* ── markdown / body detection ── */

function isBodyField(attr: FormAttribute): boolean {
    const name = normKey(getAttributeName(attr));
    return name === 'body';
}

function isMarkdownBody(formData: Record<string, any>): boolean {
    const ct = formData?.ContentType || formData?.contentType || '';
    const ctStr = typeof ct === 'string' ? ct.toLowerCase() : '';
    return ctStr === 'text/markdown' || ctStr === 'markdown';
}

function isBodyStub(value: unknown): boolean {
    return value === NON_INDEXED_PROPERTY_STUB || value === undefined || value === null;
}

/* ── Content reader layout ── */

function ContentReaderLayout({ formData, bodyValue }: { formData: Record<string, any>; bodyValue: string }) {
    const [viewMode, setViewMode] = useState<'preview' | 'source'>('preview');
    const [metaOpen, setMetaOpen] = useState(false);
    const previewScrollRef = useRef<HTMLDivElement | null>(null);

    const displayName = formData?.DisplayName || formData?.displayName || '';
    const subject = formData?.Subject || formData?.subject || '';
    const contentType = formData?.ContentType || formData?.contentType || '';
    const rawTags = formData?.Tags || formData?.tags;
    const tags: string[] = Array.isArray(rawTags)
        ? rawTags.map((t: any) => typeof t === 'string' ? t : (t?.DisplayName || t?.displayName || String(t)))
        : typeof rawTags === 'string' && rawTags.trim().startsWith('[')
            ? (() => { try { return JSON.parse(rawTags); } catch { return rawTags ? [rawTags] : []; } })()
            : rawTags ? [String(rawTags)] : [];

    return (
        <div className="flex flex-col h-full">
            {/* Document header strip */}
            <div className="rounded-lg border border-border bg-card mb-3 overflow-hidden">
                <div className="px-4 py-3">
                    <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 shrink-0 mt-0.5">
                            <FileText size={20} className="text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-base font-semibold text-foreground leading-tight truncate" title={displayName}>
                                {displayName || 'Untitled Document'}
                            </h2>
                            {subject && (
                                <p className="text-sm text-muted-foreground mt-0.5 truncate" title={subject}>
                                    {subject}
                                </p>
                            )}
                            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                                    <Code2 size={11} />
                                    {contentType || 'text/markdown'}
                                </span>
                                {tags.length > 0 && (
                                    <div className="flex items-center gap-1">
                                        <Tag size={11} className="text-muted-foreground" />
                                        {tags.slice(0, 5).map((tag, i) => (
                                            <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
                                                {tag}
                                            </span>
                                        ))}
                                        {tags.length > 5 && (
                                            <span className="text-[10px] text-muted-foreground">+{tags.length - 5}</span>
                                        )}
                                    </div>
                                )}
                                <button
                                    onClick={() => setMetaOpen(o => !o)}
                                    className="inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 transition-colors bg-transparent border-none cursor-pointer p-0"
                                >
                                    <Info size={11} />
                                    {metaOpen ? 'Hide details' : 'Show details'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Expandable metadata */}
                <div className={cn(
                    'transition-all duration-200 ease-out overflow-hidden',
                    metaOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0',
                )}>
                    <div className="px-4 pb-3 border-t border-border pt-2">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1.5">
                            {Object.entries(formData).map(([key, val]) => {
                                const k = normKey(key);
                                if (k === 'body' || k === 'entitytype') return null;
                                if (val === undefined || val === null || val === '' || val === NON_INDEXED_PROPERTY_STUB) return null;
                                const display = typeof val === 'object' ? (extractReferenceDisplayName(val) || JSON.stringify(val)) : String(val);
                                if (display.length > 200) return null;
                                return (
                                    <div key={key} className="flex flex-col py-1">
                                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{key}</span>
                                        <span className="text-xs text-foreground truncate" title={display}>{display}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Reader toolbar */}
            <div className="flex items-center gap-1 px-1 pb-2">
                <button
                    onClick={() => setViewMode('preview')}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                        viewMode === 'preview'
                            ? 'bg-card text-foreground shadow-sm border border-border'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                    )}
                >
                    <Eye size={14} />
                    Read
                </button>
                <button
                    onClick={() => setViewMode('source')}
                    className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                        viewMode === 'source'
                            ? 'bg-card text-foreground shadow-sm border border-border'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                    )}
                >
                    <Code2 size={14} />
                    Source
                </button>
            </div>

            {/* Document body -- fills remaining space */}
            <div className="flex-1 min-h-0 rounded-lg border border-border bg-card overflow-hidden">
                {viewMode === 'preview' ? (
                    <div
                        ref={previewScrollRef}
                        className="h-full overflow-auto p-6"
                        style={{ minHeight: 'calc(100vh - 340px)' }}
                    >
                        <ShadcnMarkdownPreview
                            content={bodyValue}
                            emptyMessage="No content"
                            scrollContainerRef={previewScrollRef}
                            debugAnchors
                            className="border-0 p-0"
                        />
                    </div>
                ) : (
                    <ScimAceEditor
                        mode="markdown"
                        value={bodyValue}
                        readOnly
                        name="content-reader-source"
                        height="calc(100vh - 340px)"
                        fontSize={14}
                        showPrintMargin={false}
                        highlightActiveLine={false}
                        setOptions={{
                            showLineNumbers: true,
                            tabSize: 2,
                            useWorker: false,
                        }}
                    />
                )}
            </div>
        </div>
    );
}

/* ── field cell ── */

function FieldCell({ attr, value, span }: {
    attr: FormAttribute;
    value: unknown;
    span?: boolean;
}) {
    const label = getAttributeLabel(attr);

    return (
        <div className={cn('flex flex-col gap-1 py-2', span && 'col-span-full')}>
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {label}
            </span>
            <FieldValue attr={attr} value={value} />
        </div>
    );
}

/* ── collapsible section ── */

function Section({
    meta,
    children,
    fieldCount,
}: {
    meta: SectionMeta;
    children: React.ReactNode;
    fieldCount: number;
}) {
    const [open, setOpen] = useState(meta.defaultOpen);
    const Icon = meta.icon;

    return (
        <div className="rounded-lg border border-border overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className={cn(
                    'w-full flex items-center gap-2.5 px-4 py-2.5',
                    'hover:bg-muted/50 transition-colors duration-100',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                )}
            >
                <div
                    className="flex items-center justify-center w-6 h-6 rounded shrink-0"
                    style={{ backgroundColor: `${meta.accentColor}15`, color: meta.accentColor }}
                >
                    <Icon size={14} />
                </div>
                <span className="text-sm font-semibold text-foreground">{meta.label}</span>
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
                    {fieldCount}
                </span>
                <div className="flex-1" />
                <div className="text-muted-foreground transition-transform duration-200" style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
                    <ChevronDown size={14} />
                </div>
            </button>

            <div
                className={cn(
                    'transition-all duration-200 ease-out overflow-hidden',
                    open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0',
                )}
            >
                <div className="px-4 pb-3 pt-1 border-t border-border">
                    {children}
                </div>
            </div>
        </div>
    );
}

/* ── main layout ── */

export const ShadcnDetailViewLayout: React.FC<ShadcnDetailViewLayoutProps> = ({
    schemaAttributes,
    formData,
    entityType,
    isStandard = false,
}) => {
    const isContentEntity = isContent(entityType) || isEmailTemplate(entityType);
    const hasMarkdownBody = isContentEntity && isMarkdownBody(formData);

    const bodyAttr = hasMarkdownBody
        ? schemaAttributes.find(a => isBodyField(a))
        : null;
    const bodyValue = bodyAttr
        ? getEntityValueFromARef(getAttributeName(bodyAttr), formData as Record<string, unknown>)
        : undefined;
    const bodyReady = typeof bodyValue === 'string' && !isBodyStub(bodyValue);

    if (hasMarkdownBody && bodyReady) {
        return <ContentReaderLayout formData={formData} bodyValue={bodyValue as string} />;
    }

    const sections = (() => {
        const groups: Partial<Record<FieldCategory, { attr: FormAttribute; value: unknown }[]>> = {};

        for (const attr of schemaAttributes) {
            const name = getAttributeName(attr);
            const category = categorizeField(name);

            if (isStandard && (category === 'system' || category === 'timestamp' || category === 'configuration')) continue;

            if (!groups[category]) groups[category] = [];
            groups[category]!.push({
                attr,
                value: name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : undefined,
            });
        }

        return groups;
    })();

    return (
        <div className="flex flex-col gap-3">
            {SECTION_ORDER.map(cat => {
                const items = sections[cat];
                if (!items || items.length === 0) return null;
                const meta = SECTION_META[cat];

                return (
                    <Section key={cat} meta={meta} fieldCount={items.length}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6">
                            {items.map(({ attr, value }, idx) => (
                                <FieldCell
                                    key={getAttributeName(attr) || `f-${idx}`}
                                    attr={attr}
                                    value={value}
                                    span={isLongValue(value)}
                                />
                            ))}
                        </div>
                    </Section>
                );
            })}
        </div>
    );
};
